import { useDocumentReferenceForBinaryId } from '@ctw/shared/api/fhir/binaries';
import { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import { Badge, type BadgeProps } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  type PatientRecordSearchBinaryResult,
  type PatientRecordSearchResult,
  isPatientRecordSearchBinaryResult,
} from '@ctw/shared/hooks/use-patient-record-search';
import { tw } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faArrowUpRightFromSquare, faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Resource } from 'fhir/r4';
import { trim } from 'lodash-es';
import type { KeyboardEvent } from 'react';
import { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';

interface SearchResultRowProps {
  searchResult: PatientRecordSearchResult;
}

export const SearchResultRow = ({ searchResult }: SearchResultRowProps) => {
  const { navigate } = useZuiContext();
  const { openResourceDrawer } = useResourceDrawer({
    enableDismissAndReadActions: false,
    layout: {
      showCloseButton: false,
      showDeepLinkCopyButton: true,
    },
  });

  if (searchResult.resource instanceof AllergyModel) {
    return (
      <SearchResult
        heading={searchResult.resource.display}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => openResourceDrawer({ telemetry, model })}
        details={[
          { label: 'Last Updated', value: searchResult.resource.recordedDate },
          { label: 'Reaction', value: searchResult.resource.manifestations },
        ]}
        badgeLeft={{ variant: 'info', content: searchResult.resource.resourceType }}
        badgeRight={{ variant: 'meta', content: searchResult.resource.resourceTypeTitle }}
      />
    );
  }

  if (searchResult.resource instanceof ConditionModel) {
    return (
      <SearchResult
        heading={searchResult.resource.display}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => openResourceDrawer({ telemetry, model })}
        details={[
          {
            label: 'Last Documented',
            value: searchResult.resource.recordedDate
              ? `${searchResult.resource.recordedDate} ${searchResult.resource.recorder ?? ''}`
              : 'Unknown',
          },
          {
            value: searchResult.resource.asserter,
          },
        ]}
        codes={[
          {
            label: 'ICD-10:',
            value: searchResult.resource.icd10CMEnrichments.map((coding) => coding.code).join(', '),
          },
        ]}
        badgeLeft={{ variant: 'info', content: searchResult.resource.resourceType }}
        badgeRight={{ variant: 'meta', content: searchResult.resource.resourceTypeTitle }}
      />
    );
  }

  if (isPatientRecordSearchBinaryResult(searchResult)) {
    return (
      <BinarySearchResult
        searchResult={searchResult}
        onOpenDetails={({ telemetry, model, title }) => {
          openResourceDrawer({ telemetry, model, title });
          navigate(
            pathWithParameters(
              window.location.pathname,
              {
                documentSearchQuery: trim(searchResult.allMatches[0].term, ' "'),
              },
              ['builderId'],
            ),
          );
        }}
      />
    );
  }

  if (searchResult.resource instanceof DocumentModel) {
    return (
      <SearchResult
        heading={searchResult.resource.title}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => {
          openResourceDrawer({ telemetry, model });
          navigate(
            pathWithParameters(
              window.location.pathname,
              {
                documentSearchQuery: trim(searchResult.allMatches[0].term, ' "'),
              },
              ['builderId'],
            ),
          );
        }}
        text={searchResult.allMatches[0].text}
        details={[
          {
            label: 'Encounter',
            value: searchResult.resource.encounterDate,
          },
          {
            label: 'Author',
            value: searchResult.resource.custodian,
          },
          {
            value: searchResult.resource.typeDisplay,
          },
        ]}
        codes={[{ value: searchResult.resource.contentTypeDisplay }]}
      />
    );
  }

  if (searchResult.resource instanceof MedicationStatementModel) {
    return (
      <SearchResult
        heading={searchResult.resource.display}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => openResourceDrawer({ telemetry, model })}
        details={[
          {
            label: 'Dosage',
            value: searchResult.resource.dosage,
          },
          {
            label: 'Last Filled',
            value: searchResult.resource.lastFillDate,
          },
          {
            label: 'Last Prescribed',
            value: searchResult.resource.lastPrescribedDate
              ? `${searchResult.resource.lastPrescribedDate} ${searchResult.resource.lastPrescriber}`
              : undefined,
          },
        ]}
        badgeLeft={{ variant: 'info', content: searchResult.resource.resourceType }}
        badgeRight={{ variant: 'meta', content: searchResult.resource.resourceTypeTitle }}
      />
    );
  }
  if (searchResult.resource instanceof DiagnosticReportModel) {
    return (
      <SearchResult
        heading={searchResult.resource.displayName}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => openResourceDrawer({ telemetry, model })}
        details={[
          {
            label: 'Date',
            value: searchResult.resource.dateDisplay,
          },
        ]}
        badgeLeft={{ variant: 'info', content: searchResult.resource.resourceType }}
        badgeRight={{ variant: 'meta', content: searchResult.resource.resourceTypeTitle }}
      />
    );
  }

  if (searchResult.resource instanceof ObservationModel) {
    const { dateDisplay, interpretation, resourceType, resourceTypeTitle, title, valueWithUnits } =
      searchResult.resource;
    return (
      <SearchResult
        heading={title}
        resource={searchResult.resource}
        openDetails={({ telemetry, model }) => openResourceDrawer({ telemetry, model })}
        details={[
          { label: 'Date', value: dateDisplay },
          { label: 'Value', value: valueWithUnits },
          { label: 'Interpretation', value: interpretation },
        ]}
        badgeLeft={{ variant: 'info', content: resourceType }}
        badgeRight={{ variant: 'meta', content: resourceTypeTitle }}
      />
    );
  }

  return null;
};

interface BinarySearchResultProps {
  searchResult: PatientRecordSearchBinaryResult;
  onOpenDetails: (args: {
    telemetry: ReturnType<typeof useTelemetry>;
    model: BinaryModel;
    title: string;
  }) => void;
}

const BinarySearchResult = ({ searchResult, onOpenDetails }: BinarySearchResultProps) => {
  const patientContext = usePatientContextIfAvailable();
  const hasShowSuspectedDuplicateDocumentsFeature = useFeatureFlag(
    'ctw-show-suspected-duplicate-documents',
  );

  const documentReferenceQuery = useDocumentReferenceForBinaryId({
    upid: patientContext?.patient.UPID ?? '',
    binaryId: searchResult.resource.id,
    enabled: Boolean(patientContext?.patient.UPID && searchResult.resource.id),
    excludeSuspectedDuplicates: !hasShowSuspectedDuplicateDocumentsFeature,
  });

  const documentReference = documentReferenceQuery.data;
  const title = searchResult.title ?? searchResult.resource.title;

  return (
    <SearchResult
      heading={title}
      resource={searchResult.resource}
      openDetails={({ telemetry, model }) => onOpenDetails({ telemetry, model, title })}
      text={searchResult.allMatches[0]?.text}
      details={
        documentReference
          ? [
              { label: 'Encounter', value: documentReference.encounterDate },
              { label: 'Author', value: documentReference.custodian },
              { value: documentReference.typeDisplay },
            ]
          : undefined
      }
      codes={documentReference ? [{ value: documentReference.contentTypeDisplay }] : undefined}
    />
  );
};

interface SearchResultOpenDetailsArgs<TResource extends FHIRModel<Resource>> {
  telemetry: ReturnType<typeof useTelemetry>;
  model: TResource;
}

interface SearchResultProps<TResource extends FHIRModel<Resource>> {
  resource: TResource;
  heading?: string;
  openDetails?: (args: SearchResultOpenDetailsArgs<TResource>) => void;
  text?: string;
  details?: Array<{ label?: string; value?: string }>;
  codes?: Array<{ label?: string; value?: string }>;
  badgeLeft?: BadgeProps;
  badgeRight?: BadgeProps;
}

const renderHighlightedText = (text: string) => {
  const normalizedText = sanitizeHtml(text.trim().replaceAll(/\\?\\n/gi, '<br />'), {
    allowedTags: [
      'zus_highlight',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'hr',
      'div',
      'span',
      'pre',
      'code',
      'ul',
      'ol',
      'li',
      'b',
      'i',
      'u',
      'strong',
      'em',
      'sub',
      'sup',
      'small',
    ],
    allowedAttributes: {
      '*': ['class', 'style'],
    },
    disallowedTagsMode: 'discard' as const,
    enforceHtmlBoundary: false,
  });

  let highlightedHtml = '';
  let lastIndex = 0;
  const regex = /<zus_highlight>(.*?)<\/zus_highlight>/g;
  const matches = Array.from(normalizedText.matchAll(regex));
  for (const [_, match] of matches.entries()) {
    if (match.index !== undefined && match.index > lastIndex) {
      highlightedHtml += normalizedText.slice(lastIndex, match.index);
    }
    highlightedHtml += `<span class="bg-caution-badge font-semibold">
          ${match[1]}
        </span>`;
    lastIndex = (match.index ?? 0) + match[0].length;
  }
  if (lastIndex < normalizedText.length) {
    highlightedHtml += normalizedText.slice(lastIndex);
  }

  return highlightedHtml;
};

const SearchResult = <TResource extends FHIRModel<Resource>>({
  resource,
  heading,
  openDetails,
  text,
  details,
  codes,
  badgeLeft,
  badgeRight,
}: SearchResultProps<TResource>) => {
  const telemetry = useTelemetry();
  const patientContext = usePatientContextIfAvailable();
  const { requestContext } = useCTW();

  const highlightedHtml = useMemo(() => {
    if (text === undefined) {
      return null;
    }
    return renderHighlightedText(text);
  }, [text]);

  const deepLinkUrl = useMemo(() => {
    const patient = patientContext?.patient;
    const upid = patient?.UPID;
    const builderId =
      patient?.builderId ??
      new URL(window.location.href).searchParams.get('builderId') ??
      requestContext.builderId;

    if (!(upid && builderId)) {
      return null;
    }

    const baseUrl = `${window.location.protocol}//${window.location.host}`;

    let resourcePath = `/patients/${upid}`;
    if (resource instanceof ConditionModel) {
      resourcePath = `${resourcePath}/conditions`;
    } else if (resource instanceof MedicationStatementModel) {
      resourcePath = `${resourcePath}/medications`;
    } else if (resource instanceof DiagnosticReportModel) {
      resourcePath = `${resourcePath}/diagnostics`;
    } else if (resource instanceof ObservationModel) {
      resourcePath = `${resourcePath}/vitals`;
    } else if (resource instanceof DocumentModel) {
      resourcePath = `${resourcePath}/documents`;
    } else if (resource instanceof BinaryModel) {
      resourcePath = `${resourcePath}/documents`;
    } else if (resource instanceof AllergyModel) {
      resourcePath = `${resourcePath}/allergies`;
    } else {
      resourcePath = `${resourcePath}/overview`;
    }

    let resourceParams = {};

    if (resource instanceof DocumentModel) {
      resourceParams = { documentId: resource.id };
    } else if (resource instanceof BinaryModel) {
      resourceParams = { modalResourceType: resource.resourceType, modalResourceId: resource.id };
    } else {
      resourceParams = { resourceType: resource.resourceType, resourceId: resource.id };
    }

    return `${baseUrl}${pathWithParameters(
      resourcePath,
      {
        builderId,
        ...resourceParams,
      },
      [],
    )}`;
  }, [patientContext, requestContext, resource]);

  const handleClick = () => {
    const selection = window.getSelection();
    if (!selection || selection?.type !== 'Range') {
      openDetails?.({
        telemetry,
        model: resource,
      });
    }
  };

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: We need the text to be selectable, which isn't possible with a button
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      className={tw`group relative flex w-full cursor-pointer items-start gap-4 rounded-md border border-border bg-card px-3 py-3 transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
    >
      <div className={tw`w-full min-w-0 flex-1 space-y-2`}>
        <div className={tw`flex items-start justify-between gap-4`}>
          <div className={tw`flex min-w-0 flex-1 flex-wrap items-start gap-1.5`}>
            {badgeLeft && <Badge {...badgeLeft} />}
            <h3 className={tw`max-w-full truncate font-medium text-foreground text-sm`}>
              {heading}
            </h3>
            {badgeRight && <Badge {...badgeRight} />}
          </div>
          <div className={tw`-mt-2`}>
            {deepLinkUrl && (
              <div
                className={tw`flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100`}
              >
                <Tooltip id="copy-hyperlink" content="Copy hyperlink">
                  <Button
                    type="button"
                    variant="unstyled"
                    onClick={(e) => {
                      e.stopPropagation();
                      void copyToClipboard(deepLinkUrl);
                    }}
                    ariaLabel="Copy hyperlink"
                    className={tw`inline-flex h-8 w-8 max-w-full items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`}
                  >
                    <FontAwesomeIcon icon={faLink} className={tw`h-4 w-4`} />
                  </Button>
                </Tooltip>
                <Tooltip id="open-in-new-tab" content="Open in new tab">
                  <Button
                    type="button"
                    variant="unstyled"
                    onClick={(e) => {
                      e.stopPropagation();
                      void window.open(deepLinkUrl, '_blank');
                    }}
                    ariaLabel="Open in new tab"
                    className={tw`inline-flex h-8 w-8 max-w-full items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`}
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={tw`h-4 w-4`} />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        {highlightedHtml && (
          <div
            className={tw`mb-2 rounded-md border border-info-badge bg-info-background px-3 py-2 font-medium text-sm`}
          >
            <div
              className={tw`text-start text-info-text`}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{ __html: highlightedHtml }}
            />
          </div>
        )}
        <div
          className={tw`flex w-full flex-wrap items-center justify-between gap-2 sm:flex-nowrap`}
        >
          <div
            className={tw`flex max-w-full flex-row flex-wrap items-center overflow-hidden text-left`}
          >
            {details
              ?.filter((detail) => detail.value !== undefined)
              .map((detail, index) => (
                <div
                  key={`${index}-${detail.label}-${detail.value}`}
                  className={tw`sm:-ml-[1.5px] whitespace-nowrap px-2 text-muted-foreground text-xs sm:border-divider-main sm:border-l-[1.5px]`}
                >
                  {[detail.label, detail.value].join(' ')}
                </div>
              ))}
          </div>
          {codes
            ?.filter((code) => code.value !== undefined)
            .map((code) => (
              <Badge
                key={`${code.label}-${code.value}`}
                variant="meta"
                content={[code.label, code.value].join(' ')}
              />
            ))}
        </div>
      </div>
    </div>
  );
};
